import React, {useState, useEffect, useRef} from 'react'
import {
    Flex,
    Button,
    FormControl,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    VStack,
    useDisclosure,
    HStack,
    Text,
    Icon,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Portal,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl, FormattedMessage} from 'react-intl'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import PropTypes from 'prop-types';

/* API HOOKS */
import {useBazarVoice} from '../../../integrations/bazarvoice/hooks/use-bazarvoice'

/* Icons */
import {ChevronDownType2, CheckMarkIcon} from '../../icons/index';

import {sendSortReviewEvent} from '../../../utils/gtm-utils'

const filters = [
    {name: 'All', number: 45},
    {name: 'Confort', number: 10},
    {name: 'Satisfaction', number: 5},
    {name: 'Design', number: 5},
    {name: 'Size', number: 10},
    {name: 'Color', number: 15}
]

const ReviewHeading = ({product, reviews, setReviewsData, ...otherProps}) => {
    const intl = useIntl()
    const {onOpen, onClose, isOpen} = useDisclosure()
    const [selectedOption, setSelectedOption] = useState({})
    const [sort, setSort] = useState(reviews?.options?.sort);
    const [sortOpen, setSortOpen] = useState(false)
    const [activeFilters, setActiveFilters] = useState(['All'])
    const scrollPositionRef = useRef(0)
    const desktopSortTriggerRef = useRef(null)
    const mobileSortTriggerRef = useRef(null)
    const pid = product?.master?.masterId || product?.id

    let sortingOptions = React.useMemo(() => [
        { id: 0, field: 'SubmissionTime', label: intl.formatMessage({id: "pdp_reviews.sort.mostRecent", defaultMessage: 'Most Recent'}), order: 'desc', isSelected: true },
        { id: 1, field: 'Helpfulness', label: intl.formatMessage({id: "pdp_reviews.sort.mostHelpful", defaultMessage: 'Most Helpful'}), order: 'desc', isSelected: false },
        { id: 2, field: 'Rating', label: intl.formatMessage({id: "pdp_reviews.sort.highestRanking", defaultMessage: 'Highest Ranking'}), order: 'desc', isSelected: false },
        { id: 3, field: 'SubmissionTime', label: intl.formatMessage({id: "pdp_reviews.sort.oldest", defaultMessage: 'Oldest'}), order: 'asc', isSelected: false },
        { id: 4, field: 'Helpfulness', label: intl.formatMessage({id: "pdp_reviews.sort.leastHelpful", defaultMessage: 'Least Helpful'}), order: 'asc', isSelected: false },
        { id: 5, field: 'Rating', label: intl.formatMessage({id: "pdp_reviews.sort.lowestRanking", defaultMessage: 'Lowest Ranking'}), order: 'asc', isSelected: false },
        { id: 6, field: 'HasPhotos', label: intl.formatMessage({id: "pdp_reviews.sort.photos", defaultMessage: 'Photos'}), order: 'desc', isSelected: false }
    ], []);

    const {reviews: sortedReviews} = useBazarVoice({pid, sort});

    useEffect(() => {
       setSelectedOption(sortingOptions.filter((option) => option.isSelected)[0]);

       if (sortedReviews && reviews?.options) {
            setReviewsData(() => ({
                options: {
                    ...reviews.options,
                    page: 0,
                    sort: sort
                },
                reviews: {
                    ...sortedReviews
                }
            }));
        }
    }, [sortedReviews])

    const focusSortTrigger = (triggerRef) => {
        if (!triggerRef?.current) return

        try {
            triggerRef.current.focus({preventScroll: true})
        } catch (error) {
            triggerRef.current.focus()
        }
    }

    const restoreScrollPosition = (triggerRef) => {
        if (typeof window === 'undefined') return

        // Run twice to avoid mobile browser scroll jumps caused by focus/portal teardown.
        window.requestAnimationFrame(() => {
            window.scrollTo(0, scrollPositionRef.current)
            focusSortTrigger(triggerRef)
            setTimeout(() => {
                window.scrollTo(0, scrollPositionRef.current)
                focusSortTrigger(triggerRef)
            }, 0)
        })
    }

    const handleSelect = (option, triggerRef) => {
        if (typeof window !== 'undefined') {
            scrollPositionRef.current = window.scrollY
        }
        sortingOptions = sortingOptions.map((sortOption) => {
            sortOption.isSelected = sortOption.id === option.id
            return sortOption
        });

        setSort(option.field + ':' + option.order);

        setSelectedOption(option);
        onClose();
        setSortOpen(false);
        restoreScrollPosition(triggerRef)

        // GTM Modification - only fire on user interaction
        sendSortReviewEvent(product, option.label);
    }

    const toggleFilter = (name) => {
        setActiveFilters((prev) =>
            prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
        )
    }

    // TODO - Enable when filters are ready
    const enableFilters = false

    return (
        reviews?.reviews?.Results?.length > 0 && (
        <Flex wrap={{base: "wrap", lg: "nowrap"}}
            justifyContent={{base: enableFilters ? 'space-between' : ''}}
            alignItems={{base: 'center'}}
            pb={{lg: 3}}
            borderBottom={{lg: '1px solid'}}
            borderColor={{lg: 'webTheme.strokeTertiary'}}
        >

            {/* Filters */}
            {enableFilters && (
                <Flex gap={1} 
                    alignItems="center" 
                    overflowX={{base: 'auto', lg: 'visible'}} 
                    flexWrap="nowrap" mb={{base: 9, lg: 0}}>
                    {filters.map((filter, index) => {
                        const isActive = activeFilters.includes(filter.name)
                        return (
                            <Button
                                key={`${filter.name}-${index}`}
                                // variant="webPrimary"
                                minH={8}
                                height={8}
                                padding="0 8px"
                                flex="0 0 auto"
                                bg={isActive ? 'webButton.btnPrimaryDefault' : 'webTheme.backgroundPrimaryBrand'}
                                _hover={{bg: 'webButton.btnPrimaryDefault', '& span': {color: 'webTypo.textPrimaryReverse'}}}
                                onClick={() => toggleFilter(filter.name)}
                            >
                                <Text as="span" textStyle="labelXs" textTransform={'capitalize'} color={isActive ? 'webTypo.textPrimaryReverse' : 'webTypo.textPrimary'}>
                                    {filter.name} ({filter.number})
                                </Text>
                            </Button>
                        )
                    })}
                </Flex>
            )}

            {/* Sort by */}
            <FormControl
                aria-label={intl.formatMessage({
                    id: 'pdp_reviews.heading.drawer.title.sort_by',
                    defaultMessage: 'Sort by'
                })}
                width="auto"
                {...otherProps}
            >
                {/* Sort Popover for desktop */}
                <HideOnMobile>
                    <Popover
                        variant="sort"
                        isOpen={isOpen}
                        onOpen={onOpen}
                        onClose={onClose}
                        placement="bottom-end"
                        returnFocusOnClose={false}
                    >
                        <PopoverTrigger data-testid="reviews-sort-popover-trigger">
                            <Button
                                ref={desktopSortTriggerRef}
                                data-testid="reviews-sort-trigger-desktop"
                                variant="unstyled"
                                display={{base: 'flex'}}
                                height={{base: 'auto'}}
                                fontSize={{base: 'sm'}}
                                lineHeight={{base: '100%'}}
                                fontWeight={{base: '500'}}
                                rightIcon={
                                    <Icon
                                        p={{base: '5px'}}
                                        as={ChevronDownType2}
                                        transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                                        transition="transform 0.2s"
                                    />
                                }
                                justifyContent="space-between"
                            >
                                <HStack justify="space-between">
                                    <Text fontSize={{base: 'sm'}}
                                        fontWeight={{base: '500'}}
                                        whiteSpace="nowrap">
                                        {intl.formatMessage({
                                            id: 'pdp_reviews.heading.drawer.title.sort_by',
                                            defaultMessage: 'Sort by'
                                        })}
                                    </Text>
                                </HStack>
                            </Button>
                        </PopoverTrigger>
                        <Portal>
                            <PopoverContent sx={{inset: '0px 0px auto auto'}}>
                                <PopoverBody>
                                    <VStack align="stretch" spacing={0} minW={{lg: '220px'}}>
                                        {sortingOptions?.map((option, index) => {
                                            const label = option?.label
                                            const id = option?.id
                                            return (
                                                <Button
                                                    key={id}
                                                    variant="unstyled"
                                                    fontSize={{lg: 'sm'}}
                                                    fontWeight={{lg: '500'}}
                                                    lineHeight={{lg: '100%'}}
                                                    textAlign={{lg: 'left'}}
                                                    color={{lg: 'warmBlack'}}
                                                    aria-label={`Sort by ${label}`}
                                                    h={{lg: 'auto'}}
                                                    display={{lg: 'flex'}}
                                                    alignItems={{lg: 'center'}}
                                                    pl={4}
                                                    pr={12}
                                                    py={4}
                                                    justifyContent="start"
                                                    onClick={() => handleSelect(option, desktopSortTriggerRef)}
                                                    borderBottom="1px solid"
                                                    borderColor="gray.50"
                                                    sx={{
                                                        _hover: {
                                                            backgroundColor: 'gray.50'
                                                        }
                                                    }}
                                                >
                                                    <Icon
                                                        as={CheckMarkIcon}
                                                        visibility={selectedOption?.id === id ? 'visible' : 'hidden'}
                                                        role="presentation"
                                                        aria-label={null}
                                                        w={{lg: '20px'}}
                                                        h={{lg: '20px'}}
                                                        mr={{lg: 4}}
                                                    />
                                                    {label}
                                                </Button>
                                            )
                                        })}
                                    </VStack>
                                </PopoverBody>
                            </PopoverContent>
                        </Portal>
                    </Popover>
                </HideOnMobile>

                {/* Sort drawer for mobile */}
                <HideOnDesktop>
                    <Button
                        ref={mobileSortTriggerRef}
                        data-testid="reviews-sort-trigger-mobile"
                        variant="unstyled"
                        display={{base: 'flex'}}
                        height={{base: 'auto'}}
                        fontSize={{base: 'sm'}}
                        lineHeight={{base: '100%'}}
                        fontWeight={{base: '500'}}
                        rightIcon={
                            <Icon
                                p={{base: '5px'}}
                                as={ChevronDownType2}
                                transform={sortOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                                transition="transform 0.2s"
                            />
                        }
                        justifyContent="space-between"
                        onMouseDown={() => {
                            if (typeof window !== 'undefined') {
                                scrollPositionRef.current = window.scrollY
                            }
                        }}
                        onClick={() => setSortOpen(true)}
                    >
                        <HStack justify="space-between">
                            <Text whiteSpace="nowrap">
                                <FormattedMessage
                                    defaultMessage="Sort by"
                                    id="pdp_reviews.heading.drawer.title.sort_by"
                                />
                            </Text>
                        </HStack>
                    </Button>
                    <Drawer
                        finalFocusRef={mobileSortTriggerRef}
                        placement="bottom"
                        isOpen={sortOpen}
                        onClose={() => setSortOpen(false)}
                        onCloseComplete={() => restoreScrollPosition(mobileSortTriggerRef)}
                        size="sm"
                        motionPreset="slideInBottom"
                        scrollBehavior="inside"
                        isFullHeight={false}
                        height="50%"
                        blockScrollOnMount
                    >
                        <DrawerOverlay />
                        <DrawerContent marginTop={0}
                            pt={{base: 6}}>
                            <DrawerHeader boxShadow="none">
                                <Text color={{base: 'grey.800'}}
                                    fontSize={{base: 'sm'}}
                                    padding={{base: '0 0 16px 32px'}}
                                    fontWeight={{base: 400}}
                                    lineHeight={{base: '100%'}}>
                                    <FormattedMessage
                                        defaultMessage="Sort by"
                                        id="pdp_reviews.heading.drawer.title.sort_by"
                                    />
                                </Text>
                            </DrawerHeader>
                            <DrawerCloseButton top={{base: '24px'}}
                                right={{base: '30px'}}
                                w={{base: '20px'}}
                                h={{base: '20px'}}
                                color={{base: 'warmBlack'}}
                            />
                            <DrawerBody minH={'386px'} p={0}>
                                <VStack align="stretch" spacing={0}>
                                    {sortingOptions?.map((option, index) => {
                                        const label = option?.label
                                        const id = option?.id
                                        return (
                                            <Button
                                                variant="unstyled"
                                                key={id}
                                                fontSize={{base: 'md'}}
                                                fontWeight={{base: '500'}}
                                                lineHeight={{base: '100%'}}
                                                textAlign={{base: 'left'}}
                                                color={{base: 'warmBlack'}}
                                                h={{base: 'auto'}}
                                                px={{base: 8}}
                                                py={{base: 4}}
                                                display={{base: 'flex'}}
                                                justifyContent={{base: 'start'}}
                                                alignItems={{base: 'center'}}
                                                onClick={() => handleSelect(option, mobileSortTriggerRef)}
                                                borderBottom={{base: '1px solid'}}
                                                borderColor={{base: 'gray.50'}}
                                                leftIcon={selectedOption?.id === id ?
                                                    <Icon
                                                        as={CheckMarkIcon}
                                                        w={{base: '20px'}}
                                                        h={{base: '20px'}}
                                                        mr={{base: 2}}
                                                    /> : undefined
                                                }
                                            >
                                                {label}
                                            </Button>
                                        )
                                    })}
                                </VStack>
                            </DrawerBody>
                        </DrawerContent>
                    </Drawer>
                </HideOnDesktop>
            </FormControl>
        </Flex>)
    )
}

ReviewHeading.propTypes = {
    product: PropTypes.object,
    reviews: PropTypes.object,
    setReviewsData: PropTypes.func,
    otherProps: PropTypes.object
};

export default ReviewHeading
